import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Info } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RechartsTooltip
} from 'recharts'
import { Card, Tooltip } from '../../ui'
import { PublishedBroadcast } from '../../../services/broadcastService'
import { SponsorRollup } from './BroadcastSponsorCards'
import { formatEventDate } from './broadcastCustomerFormat'
import { secs, pct } from '../broadcastReviewConstants'
import { currencyOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'
import { BroadcastOrigin } from './broadcastOrigin'

/** One broadcast's delivery for one sponsor: a bar, a table row. */
export interface SeasonRow {
  broadcast: PublishedBroadcast
  area: number
  /** Null when this broadcast is not priced. */
  value: number | null
  /** Null until the broadcast's exposure payload has loaded. */
  ms: number | null
  peak: number | null
  /** The sponsor's largest brand row here, for the drill-down link. */
  brandKey: string | null
}

const OTHER_GREY = '#d1d5db'

const SectionTitle: React.FC<{ title: string; tooltip: string; right?: React.ReactNode }> = ({ title, tooltip, right }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-1.5">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <Tooltip content={tooltip} placement="top" maxWidth={260}>
        <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
      </Tooltip>
    </div>
    {right}
  </div>
)

const shortDate = (d: string | null) => {
  if (!d) return '—'
  const [y, m, day] = d.slice(0, 10).split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, day ?? 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const linkFor = (r: SeasonRow) => r.brandKey
  ? `/broadcasts/${r.broadcast.id}/brands/${encodeURIComponent(r.brandKey)}`
  : `/broadcasts/${r.broadcast.id}/exposure`

/**
 * Value per broadcast in air-date order: is delivery steady, growing or
 * tailing off. One series, one axis. In value mode an unpriced broadcast keeps
 * its slot on the axis with no bar, so a gap reads as "not priced" rather
 * than the season silently skipping a game.
 */
export const SponsorSeasonChart: React.FC<{
  rows: SeasonRow[]; priced: boolean; currency: string; color: string; origin: BroadcastOrigin
}> = ({ rows, priced, currency, color, origin }) => {
  const navigate = useNavigate()
  const fmt = (n: number) => priced ? formatMoney(n, currency) : formatAreaSeconds(n)
  const data = [...rows]
    .sort((a, b) => (a.broadcast.event_date ?? '').localeCompare(b.broadcast.event_date ?? ''))
    .map((r) => ({ row: r, label: shortDate(r.broadcast.event_date), amount: priced ? r.value : r.area }))

  return (
    <Card padding="md">
      <SectionTitle
        title={priced ? 'Value by broadcast' : 'Area-seconds by broadcast'}
        tooltip={priced
          ? 'This sponsor\'s media value on each broadcast, in air-date order. A slot with no bar is a broadcast that is not priced yet. Click a bar to open that broadcast.'
          : 'Nothing is priced yet, so this shows sponsored area-seconds on each broadcast. Click a bar to open that broadcast.'}
      />
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false}
                   axisLine={{ stroke: '#e5e7eb' }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={64}
                   tickFormatter={(v: number) => fmt(v)} />
            <RechartsTooltip
              cursor={{ fill: '#f8fafc' }}
              contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#e5e7eb' }}
              labelFormatter={(_, p) => (p?.[0]?.payload as { row: SeasonRow } | undefined)?.row.broadcast.title ?? ''}
              formatter={(v) => [v == null ? 'Not priced' : fmt(Number(v)), priced ? 'Media value' : 'Area-seconds']}
            />
            <Bar dataKey="amount" maxBarSize={36} radius={[4, 4, 0, 0]} isAnimationActive={false}
                 cursor="pointer"
                 onClick={(d: { row: SeasonRow }) => navigate(linkFor(d.row), { state: { origin } })}>
              {data.map((d) => <Cell key={d.row.broadcast.id} fill={color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

/**
 * Where this sponsor ranks among all sponsors, on the same measure as the
 * Share KPI. Only this sponsor wears colour; the rest are grey context.
 */
export const SponsorShareOfVoice: React.FC<{
  sponsors: SponsorRollup[]; selfId: string; priced: boolean; currency: string; color: string
}> = ({ sponsors, selfId, priced, currency, color }) => {
  const navigate = useNavigate()
  const amountOf = (s: SponsorRollup) => priced ? (s.value ?? 0) : s.area
  const ranked = [...sponsors].sort((a, b) => amountOf(b) - amountOf(a))
  const total = ranked.reduce((n, s) => n + amountOf(s), 0)
  const rank = ranked.findIndex((s) => s.id === selfId) + 1
  const max = Math.max(...ranked.map(amountOf), 1e-9)
  const fmt = (n: number) => priced ? formatMoney(n, currency) : formatAreaSeconds(n)

  // Top eight, plus this sponsor if it ranks lower, so it is always on show.
  const shown = ranked.slice(0, 8)
  if (rank > 8) shown.push(ranked[rank - 1]!)

  return (
    <Card padding="md">
      <SectionTitle
        title="Share of voice"
        tooltip={priced
          ? 'Every sponsor\'s media value across the library. Click a sponsor to open theirs.'
          : 'Nothing is priced yet, so sponsors are ranked by area-seconds. Click a sponsor to open theirs.'}
        right={<span className="text-xs font-semibold text-gray-700">#{rank} of {ranked.length}</span>}
      />
      <ul className="space-y-1.5">
        {shown.map((s, i) => {
          const self = s.id === selfId
          const position = rank > 8 && i === shown.length - 1 ? rank : i + 1
          return (
            <li key={s.id}>
              <button type="button" onClick={() => !self && navigate(`/broadcasts/sponsors/${s.id}`)}
                      className={`w-full text-left rounded px-1.5 py-1 ${self ? 'bg-gray-50 cursor-default' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="w-5 text-right text-gray-400 tabular-nums">{position}</span>
                  <span className={`truncate ${self ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{s.name}</span>
                  <span className="ml-auto tabular-nums font-medium text-gray-900">{fmt(amountOf(s))}</span>
                  <span className="w-9 text-right tabular-nums text-gray-500">
                    {total > 0 ? `${Math.round((amountOf(s) / total) * 100)}%` : '—'}
                  </span>
                </div>
                <span className="ml-7 mt-1 block h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <span className="block h-full rounded-full"
                        style={{ width: `${Math.max((amountOf(s) / max) * 100, 1)}%`, backgroundColor: self ? color : OTHER_GREY }} />
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

const th = 'px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider'

/** Every broadcast this sponsor appeared in, newest first; a row opens it. */
export const SponsorBroadcastTable: React.FC<{
  rows: SeasonRow[]; currency: string; origin: BroadcastOrigin
}> = ({ rows, currency, origin }) => {
  const navigate = useNavigate()
  const sorted = [...rows].sort((a, b) => (b.broadcast.event_date ?? '').localeCompare(a.broadcast.event_date ?? ''))
  return (
    <Card padding="sm">
      <div className="px-1 pt-1">
        <SectionTitle title="Broadcasts" tooltip="Each broadcast this sponsor appeared in. Open one to see every appearance with its frame and Instant Replay." />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={`${th} text-left`}>Broadcast</th>
              <th className={`${th} text-left`}>Aired</th>
              <th className={`${th} text-right`}>On screen</th>
              <th className={`${th} text-right`}>Peak logo size</th>
              <th className={`${th} text-right`}>Area-seconds</th>
              <th className={`${th} text-right`}>Media value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((r) => (
              <tr key={r.broadcast.id} onClick={() => navigate(linkFor(r), { state: { origin } })}
                  className="hover:bg-blue-50/60 cursor-pointer transition-colors group">
                <td className="px-3 py-2.5">
                  <p className="text-xs font-semibold text-gray-900 group-hover:text-blue-700">{r.broadcast.title}</p>
                  {r.broadcast.venue_name && <p className="text-[11px] text-gray-500">{r.broadcast.venue_name}</p>}
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-900 whitespace-nowrap">
                  {formatEventDate(r.broadcast.event_date) ?? '—'}
                </td>
                <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-900">
                  {r.ms == null ? '—' : secs(r.ms)}
                </td>
                <td className="px-3 py-2.5 text-xs text-right tabular-nums text-gray-900">{pct(r.peak)}</td>
                <td className="px-3 py-2.5 text-xs text-right tabular-nums font-medium text-gray-900">
                  {formatAreaSeconds(r.area)}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {r.value === null ? (
                    <span className="text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">Not priced</span>
                  ) : (
                    <span className="text-sm font-bold text-emerald-800 tabular-nums">
                      {formatMoney(r.value, currencyOf(r.broadcast) ?? currency)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
